<template>
  <div class="step-card">
    <!-- Заголовок -->
    <div class="step-header">
      <div class="step-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="6"
            cy="12"
            r="2.5"
            stroke="currentColor"
            stroke-width="1.8"
          />

          <circle
            cx="17.5"
            cy="6"
            r="2.5"
            stroke="currentColor"
            stroke-width="1.8"
          />

          <circle
            cx="17.5"
            cy="18"
            r="2.5"
            stroke="currentColor"
            stroke-width="1.8"
          />

          <path
            d="m8.2 10.8 7.1-3.7M8.2 13.2l7.1 3.7"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
          />
        </svg>
      </div>

      <div class="step-heading">
        <span class="step-label">
          Онлайн-присутствие
        </span>

        <h3 class="step-title">
          Социальные сети
        </h3>
      </div>

      <span class="optional-badge">
        Необязательно
      </span>
    </div>

    <p class="description">
      Добавьте ссылки на социальные сети компании. Они помогут кандидатам
      узнать больше о вашей команде и повысят доверие к профилю.
    </p>

    <!-- Статистика -->
    <div class="socials-heading">
      <span class="socials-title">
        Ссылки компании
      </span>

      <span
        class="socials-counter"
        :class="{ active: filledCount > 0 }"
      >
        {{ filledCount }} из {{ socials.length }}
      </span>
    </div>

    <!-- Поля -->
    <div class="fields">
      <div
        v-for="item in socials"
        :key="item.key"
        class="social-field-wrapper"
      >
        <div
          class="social-field"
          :class="{
            focused: focusedSocial === item.key,
            filled: hasValue(item.key),
            valid: hasValue(item.key) && isSocialValid(item),
            invalid: showError(item)
          }"
          :style="{ '--brand-color': item.color }"
        >
          <div class="social-icon-wrapper">
            <img
              :src="item.icon"
              :alt="item.name"
              class="social-icon"
            />
          </div>

          <div class="separator"></div>

          <div class="input-content">
            <label
              class="field-label"
              :for="`social-${item.key}`"
            >
              {{ item.name }}
            </label>

            <input
              :id="`social-${item.key}`"
              v-model="model.socials[item.key]"
              type="text"
              class="social-input"
              :placeholder="item.placeholder"
              inputmode="url"
              autocomplete="off"
              spellcheck="false"
              @focus="focusedSocial = item.key"
              @blur="handleBlur(item)"
              @input="handleInput(item)"
            />
          </div>

          <!-- Loader при обработке -->
          <Transition name="action">
            <div
              v-if="normalizingSocial === item.key"
              class="normalizing-loader"
            ></div>
          </Transition>

          <!-- Успешно -->
          <Transition name="action">
            <div
              v-if="
                hasValue(item.key) &&
                isSocialValid(item) &&
                normalizingSocial !== item.key
              "
              class="valid-icon"
            >
              <svg
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="m5.5 10.2 2.8 2.8 6.2-6.2"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </Transition>

          <!-- Очистить -->
          <button
            v-if="
              hasValue(item.key) &&
              focusedSocial === item.key &&
              normalizingSocial !== item.key
            "
            type="button"
            class="clear-button"
            :aria-label="`Очистить ${item.name}`"
            @mousedown.prevent
            @click="clearSocial(item.key)"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M6.5 6.5 13.5 13.5M13.5 6.5 6.5 13.5"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>

        <Transition name="message">
          <div
            v-if="showError(item)"
            class="field-error"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="10"
                cy="10"
                r="8"
                stroke="currentColor"
                stroke-width="1.6"
              />

              <path
                d="M10 6.3v4.4M10 13.6v.1"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>

            Укажите корректную ссылку или имя пользователя
          </div>
        </Transition>
      </div>
    </div>

    <!-- Подсказка -->
    <div class="socials-hint">
      <div class="hint-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="12"
            cy="12"
            r="9"
            stroke="currentColor"
            stroke-width="1.7"
          />

          <path
            d="M12 11v5M12 7.5v.2"
            stroke="currentColor"
            stroke-width="1.9"
            stroke-linecap="round"
          />
        </svg>
      </div>

      <p>
        Можно вставить полную ссылку или просто имя пользователя.
        Например, <strong>@company</strong> автоматически превратится
        в полноценную ссылку.
      </p>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  reactive,
  ref,
  watch
} from "vue";

import facebookIcon from "../../assets/facebook-profile.png";
import instagramIcon from "../../assets/instagram-profile.png";
import linkedinIcon from "../../assets/linkedin-profile.png";
import twitterIcon from "../../assets/x-profile.png";
import telegramIcon from "../../assets/telegram-profile.png";
import youtubeIcon from "../../assets/youtube-profile.png";

const model = defineModel({
  required: true
});

const emit = defineEmits([
  "valid",
  "not-valid"
]);

const focusedSocial = ref(null);
const normalizingSocial = ref(null);

const touchedSocials = reactive({
  facebook: false,
  instagram: false,
  linkedin: false,
  twitter: false,
  telegram: false,
  youtube: false
});

const socials = [
  {
    key: "facebook",
    name: "Facebook",
    icon: facebookIcon,
    placeholder: "facebook.com/company",
    baseURL: "https://facebook.com/",
    domains: ["facebook.com", "www.facebook.com"],
    color: "#1877F2"
  },
  {
    key: "instagram",
    name: "Instagram",
    icon: instagramIcon,
    placeholder: "instagram.com/company",
    baseURL: "https://instagram.com/",
    domains: ["instagram.com", "www.instagram.com"],
    color: "#E4405F"
  },
  {
    key: "linkedin",
    name: "LinkedIn",
    icon: linkedinIcon,
    placeholder: "linkedin.com/company/company",
    baseURL: "https://linkedin.com/company/",
    domains: ["linkedin.com", "www.linkedin.com"],
    color: "#0A66C2"
  },
  {
    key: "twitter",
    name: "X",
    icon: twitterIcon,
    placeholder: "x.com/company",
    baseURL: "https://x.com/",
    domains: [
      "x.com",
      "www.x.com",
      "twitter.com",
      "www.twitter.com"
    ],
    color: "#111111"
  },
  {
    key: "telegram",
    name: "Telegram",
    icon: telegramIcon,
    placeholder: "t.me/company",
    baseURL: "https://t.me/",
    domains: [
      "t.me",
      "www.t.me",
      "telegram.me",
      "www.telegram.me"
    ],
    color: "#229ED9"
  },
  {
    key: "youtube",
    name: "YouTube",
    icon: youtubeIcon,
    placeholder: "youtube.com/@company",
    baseURL: "https://youtube.com/@",
    domains: ["youtube.com", "www.youtube.com"],
    color: "#FF0000"
  }
];

initializeSocials();

const filledCount = computed(() =>
  socials.filter(item => hasValue(item.key)).length
);

const allSocialsValid = computed(() =>
  socials.every(item => isSocialValid(item))
);

function initializeSocials() {
  if (
    !model.value.socials ||
    typeof model.value.socials !== "object"
  ) {
    model.value.socials = {};
  }

  socials.forEach(item => {
    if (typeof model.value.socials[item.key] !== "string") {
      model.value.socials[item.key] = "";
    }
  });
}

function hasValue(key) {
  return Boolean(
    (model.value.socials[key] || "").trim()
  );
}

function isSocialValid(item) {
  const value =
    (model.value.socials[item.key] || "").trim();

  /*
    Пустое поле валидно, поскольку шаг необязательный.
  */
  if (!value) {
    return true;
  }

  /*
    Проверяем полную ссылку.
  */
  if (/^https?:\/\//i.test(value)) {
    try {
      const url = new URL(value);
      const hostname = url.hostname.toLowerCase();

      return item.domains.some(domain =>
        hostname === domain ||
        hostname.endsWith(`.${domain}`)
      );
    } catch {
      return false;
    }
  }

  /*
    Разрешаем вставить ссылку без протокола.
  */
  if (value.includes(".")) {
    try {
      const url = new URL(`https://${value}`);
      const hostname = url.hostname.toLowerCase();

      return item.domains.some(domain =>
        hostname === domain ||
        hostname.endsWith(`.${domain}`)
      );
    } catch {
      return false;
    }
  }

  /*
    Или обычное имя пользователя: @company / company.
  */
  return /^@?[a-zA-Z0-9._-]{2,100}$/.test(value);
}

function showError(item) {
  return (
    touchedSocials[item.key] &&
    hasValue(item.key) &&
    !isSocialValid(item)
  );
}

function handleInput(item) {
  touchedSocials[item.key] = true;
  emitValidationState();
}

function handleBlur(item) {
  focusedSocial.value = null;
  touchedSocials[item.key] = true;

  normalizeSocial(item);
  emitValidationState();
}

function normalizeSocial(item) {
  const rawValue =
    (model.value.socials[item.key] || "").trim();

  if (!rawValue || !isSocialValid(item)) {
    model.value.socials[item.key] = rawValue;
    return;
  }

  normalizingSocial.value = item.key;

  let normalizedValue = rawValue;

  if (/^https?:\/\//i.test(rawValue)) {
    normalizedValue = rawValue;
  } else if (rawValue.includes(".")) {
    normalizedValue = `https://${rawValue}`;
  } else {
    const username = rawValue.replace(/^@/, "");

    normalizedValue =
      `${item.baseURL}${username}`;
  }

  model.value.socials[item.key] = normalizedValue;

  window.setTimeout(() => {
    if (normalizingSocial.value === item.key) {
      normalizingSocial.value = null;
    }
  }, 300);
}

function clearSocial(key) {
  model.value.socials[key] = "";
  touchedSocials[key] = false;
  normalizingSocial.value = null;

  emitValidationState();
}

function emitValidationState() {
  if (allSocialsValid.value) {
    emit("valid");
  } else {
    emit("not-valid");
  }
}

watch(
  () =>
    socials.map(item =>
      model.value.socials[item.key]
    ),
  () => {
    emitValidationState();
  },
  {
    deep: true,
    immediate: true
  }
);
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.step-card {
  width: 100%;
  padding: 28px;

  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(48, 132, 255, 0.075),
      transparent 250px
    ),
    #ffffff;

  animation: card-appearance 0.45s ease both;
}

/* Header */

.step-header {
  display: flex;
  align-items: center;
  gap: 13px;

  margin-bottom: 18px;
}

.step-icon {
  width: 44px;
  height: 44px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #ffffff;

  background:
    linear-gradient(
      145deg,
      #36a1ff 0%,
      #4078f3 55%,
      #625bea 100%
    );

  border-radius: 14px;

  box-shadow:
    0 8px 18px rgba(48, 126, 246, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.24);
}

.step-icon svg {
  width: 23px;
  height: 23px;
}

.step-heading {
  min-width: 0;

  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
}

.step-label {
  color: #8b94a4;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.step-title {
  margin: 0;

  color: #20242c;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 19px;
  line-height: 1.25;
}

.optional-badge {
  flex-shrink: 0;

  padding: 7px 10px;

  color: #687385;
  background: rgba(113, 125, 145, 0.08);

  border: 1px solid rgba(113, 125, 145, 0.1);
  border-radius: 999px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
}

.description {
  max-width: 680px;

  margin: 0 0 22px;

  color: #697282;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  line-height: 1.65;
}

/* Social heading */

.socials-heading {
  width: 100%;
  max-width: 620px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  margin-bottom: 9px;
}

.socials-title {
  color: #424957;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 12px;
}

.socials-counter {
  flex-shrink: 0;

  padding: 5px 8px;

  color: #8b93a0;
  background: #f3f5f8;

  border-radius: 8px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;

  transition:
    color 0.2s ease,
    background-color 0.2s ease;
}

.socials-counter.active {
  color: #397fdc;
  background: rgba(57, 133, 235, 0.09);
}

/* Fields */

.fields {
  width: 100%;
  max-width: 620px;

  display: flex;
  flex-direction: column;
  gap: 11px;
}

.social-field-wrapper {
  width: 100%;
}

.social-field {
  position: relative;

  width: 100%;
  min-height: 62px;

  display: flex;
  align-items: center;

  overflow: hidden;

  background: #f7f8fa;

  border: 1px solid #e2e5ea;
  border-radius: 15px;

  box-shadow:
    inset 0 1px 2px rgba(15, 23, 42, 0.02);

  transition:
    background-color 0.22s ease,
    border-color 0.22s ease,
    box-shadow 0.22s ease,
    transform 0.22s ease;
}

.social-field:hover {
  background: #fafbfc;
  border-color: #d6dae1;
}

.social-field.focused {
  background: #ffffff;
  border-color: var(--brand-color);

  box-shadow:
    0 0 0 4px
    color-mix(
      in srgb,
      var(--brand-color) 12%,
      transparent
    ),
    0 8px 23px rgba(15, 23, 42, 0.06);

  transform: translateY(-1px);
}

.social-field.valid:not(.focused) {
  border-color: rgba(43, 161, 101, 0.34);
}

.social-field.invalid {
  background: rgba(255, 246, 246, 0.9);
  border-color: #e15b5b;

  box-shadow:
    0 0 0 4px rgba(225, 91, 91, 0.08);
}

.social-icon-wrapper {
  width: 44px;
  height: 44px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  margin-left: 9px;

  background: #ffffff;

  border: 1px solid rgba(20, 35, 55, 0.06);
  border-radius: 12px;

  transition:
    background-color 0.22s ease,
    box-shadow 0.22s ease,
    transform 0.22s ease;
}

.social-field.focused .social-icon-wrapper,
.social-field.filled .social-icon-wrapper {
  box-shadow:
    0 5px 13px
    color-mix(
      in srgb,
      var(--brand-color) 17%,
      transparent
    );
}

.social-field.focused .social-icon-wrapper {
  transform: scale(1.03);
}

.social-icon {
  width: 23px;
  height: 23px;

  object-fit: contain;

  opacity: 0.38;
  filter: grayscale(0.85);

  transition:
    opacity 0.22s ease,
    filter 0.22s ease,
    transform 0.22s ease;
}

.social-field.filled .social-icon,
.social-field.focused .social-icon {
  opacity: 1;
  filter: grayscale(0);
}

.social-field.focused .social-icon {
  transform: scale(1.06);
}

.separator {
  width: 1px;
  height: 30px;

  flex-shrink: 0;

  margin: 0 13px;

  background: #e1e4e9;

  transition:
    background-color 0.22s ease;
}

.social-field.focused .separator {
  background:
    color-mix(
      in srgb,
      var(--brand-color) 28%,
      transparent
    );
}

.input-content {
  min-width: 0;

  display: flex;
  flex: 1;
  flex-direction: column;

  padding: 9px 0;
}

.field-label {
  margin-bottom: 2px;

  color: #8b93a0;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
  font-weight: 600;
  line-height: 1.2;

  transition:
    color 0.2s ease;
}

.social-field.focused .field-label {
  color: var(--brand-color);
}

.social-input {
  min-width: 0;
  width: 100%;

  padding: 2px 8px 2px 0;

  color: #262c36;
  caret-color: var(--brand-color);

  background: transparent;

  border: none;
  outline: none;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  line-height: 1.4;
}

.social-input::placeholder {
  color: #a8aeba;
}

/* Valid */

.valid-icon {
  width: 25px;
  height: 25px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  margin-right: 13px;

  color: #28a46b;
  background: rgba(40, 164, 107, 0.1);

  border-radius: 50%;
}

.valid-icon svg {
  width: 15px;
  height: 15px;
}

/* Clear */

.clear-button {
  width: 27px;
  height: 27px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  margin-right: 12px;
  padding: 0;

  cursor: pointer;

  color: #8c94a0;
  background: #eceef2;

  border: none;
  border-radius: 9px;

  transition:
    color 0.2s ease,
    background-color 0.2s ease,
    transform 0.2s ease;
}

.clear-button:hover {
  color: #ffffff;
  background: #df555a;

  transform: scale(1.06);
}

.clear-button svg {
  width: 16px;
  height: 16px;
}

/* Loader */

.normalizing-loader {
  width: 21px;
  height: 21px;

  flex-shrink: 0;

  margin-right: 14px;

  border: 2px solid rgba(67, 136, 245, 0.16);
  border-top-color: #4388f5;
  border-radius: 50%;

  animation: spin 0.65s linear infinite;
}

/* Error */

.field-error {
  display: flex;
  align-items: flex-start;
  gap: 6px;

  margin: 6px 0 0 12px;

  color: #d84e4e;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.4;
}

.field-error svg {
  width: 14px;
  height: 14px;

  flex-shrink: 0;
}

/* Hint */

.socials-hint {
  width: 100%;
  max-width: 620px;

  display: flex;
  align-items: flex-start;
  gap: 10px;

  margin-top: 21px;
  padding: 13px 14px;

  color: #667084;

  background:
    linear-gradient(
      135deg,
      rgba(53, 132, 247, 0.06),
      rgba(83, 104, 232, 0.035)
    );

  border: 1px solid rgba(53, 132, 247, 0.1);
  border-radius: 13px;
}

.hint-icon {
  width: 18px;
  height: 18px;

  flex-shrink: 0;

  color: #4388f5;
}

.hint-icon svg {
  width: 18px;
  height: 18px;
}

.socials-hint p {
  margin: 0;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
  line-height: 1.55;
}

.socials-hint strong {
  color: #407dcc;

  font-family: "Eastman-Bold", sans-serif;
}

/* Animations */

@keyframes card-appearance {
  from {
    opacity: 0;
    transform: translateY(7px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.action-enter-active,
.action-leave-active,
.message-enter-active,
.message-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.action-enter-from,
.action-leave-to {
  opacity: 0;

  transform: scale(0.7);
}

.message-enter-from,
.message-leave-to {
  opacity: 0;

  transform: translateY(-3px);
}

/* ==========================================
   Dark mode
   ========================================== */

html[data-theme="dark"] .step-card {
  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(54, 132, 255, 0.09),
      transparent 250px
    ),
    #1c1c1e;
}

html[data-theme="dark"] .step-title {
  color: #f2f2f4;
}

html[data-theme="dark"] .step-label {
  color: #81858e;
}

html[data-theme="dark"] .optional-badge {
  color: #a3aab6;

  background: rgba(255, 255, 255, 0.055);
  border-color: rgba(255, 255, 255, 0.07);
}

html[data-theme="dark"] .description {
  color: #a7aab1;
}

html[data-theme="dark"] .socials-title {
  color: #d8dae0;
}

html[data-theme="dark"] .socials-counter {
  color: #8f96a2;
  background: #292a2f;
}

html[data-theme="dark"] .socials-counter.active {
  color: #73a7f5;
  background: rgba(67, 136, 245, 0.12);
}

html[data-theme="dark"] .social-field {
  background: #292a2e;
  border-color: #414349;

  box-shadow:
    inset 0 1px 2px rgba(0, 0, 0, 0.2);
}

html[data-theme="dark"] .social-field:hover {
  background: #2c2d31;
  border-color: #4a4c53;
}

html[data-theme="dark"] .social-field.focused {
  background: #2d2f34;
  border-color: var(--brand-color);

  box-shadow:
    0 0 0 4px
    color-mix(
      in srgb,
      var(--brand-color) 13%,
      transparent
    ),
    0 9px 24px rgba(0, 0, 0, 0.18);
}

html[data-theme="dark"]
.social-field.valid:not(.focused) {
  border-color: rgba(61, 192, 126, 0.62);
}

html[data-theme="dark"] .social-field.invalid {
  background: rgba(91, 37, 40, 0.22);
  border-color: #d85b60;

  box-shadow:
    0 0 0 4px rgba(216, 91, 96, 0.12);
}

html[data-theme="dark"] .social-icon-wrapper {
  background: #323237;
  border-color: rgba(255, 255, 255, 0.07);
}

html[data-theme="dark"]
.social-field.focused
.social-icon-wrapper,
html[data-theme="dark"]
.social-field.filled
.social-icon-wrapper {
  box-shadow:
    0 5px 13px
    color-mix(
      in srgb,
      var(--brand-color) 22%,
      transparent
    );
}

html[data-theme="dark"] .separator {
  background: #434349;
}

html[data-theme="dark"]
.social-field.focused
.separator {
  background:
    color-mix(
      in srgb,
      var(--brand-color) 34%,
      transparent
    );
}

html[data-theme="dark"] .field-label {
  color: #858b96;
}

html[data-theme="dark"]
.social-field.focused
.field-label {
  color: var(--brand-color);
}

html[data-theme="dark"] .social-input {
  color: #f2f2f4;
}

html[data-theme="dark"]
.social-input::placeholder {
  color: #70747c;
}

html[data-theme="dark"] .valid-icon {
  color: #51c989;

  background: rgba(61, 192, 126, 0.14);
}

html[data-theme="dark"] .clear-button {
  color: #a0a6b0;
  background: #3b3b41;
}

html[data-theme="dark"] .clear-button:hover {
  color: #ffffff;
  background: #d95358;
}

html[data-theme="dark"] .normalizing-loader {
  border-color: rgba(103, 162, 255, 0.2);
  border-top-color: #67a2ff;
}

html[data-theme="dark"] .field-error {
  color: #ff8b94;
}

html[data-theme="dark"] .socials-hint {
  color: #9ba2ae;

  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.1),
      rgba(83, 104, 232, 0.06)
    );

  border-color: rgba(74, 145, 246, 0.16);
}

html[data-theme="dark"] .hint-icon {
  color: #67a2ff;
}

html[data-theme="dark"] .socials-hint strong {
  color: #79a9ef;
}

/* ==========================================
   Compact laptop
   1366x768 / 1366x788
   ========================================== */

@media (min-width: 701px) and (max-height: 820px) {
  .step-card {
    padding: 15px 22px;
  }

  /* Header */

  .step-header {
    gap: 10px;

    margin-bottom: 8px;
  }

  .step-icon {
    width: 36px;
    height: 36px;

    border-radius: 11px;
  }

  .step-icon svg {
    width: 19px;
    height: 19px;
  }

  .step-heading {
    gap: 1px;
  }

  .step-label {
    font-size: 8px;
  }

  .step-title {
    font-size: 15px;
    line-height: 1.15;
  }

  .optional-badge {
    padding: 5px 8px;

    font-size: 8px;
  }

  .description {
    max-width: 650px;

    margin-bottom: 9px;

    font-size: 10px;
    line-height: 1.35;
  }

  /* Heading */

  .socials-heading {
    margin-bottom: 5px;
  }

  .socials-title {
    font-size: 10px;
  }

  .socials-counter {
    padding: 4px 7px;

    border-radius: 6px;

    font-size: 8px;
  }

  /* Fields */

  .fields {
    gap: 6px;
  }

  .social-field {
    min-height: 46px;

    border-radius: 11px;
  }

  .social-field.focused {
    box-shadow:
      0 0 0 3px
      color-mix(
        in srgb,
        var(--brand-color) 11%,
        transparent
      ),
      0 5px 15px rgba(15, 23, 42, 0.045);
  }

  .social-icon-wrapper {
    width: 32px;
    height: 32px;

    margin-left: 7px;

    border-radius: 9px;
  }

  .social-icon {
    width: 18px;
    height: 18px;
  }

  .separator {
    height: 24px;

    margin: 0 10px;
  }

  .input-content {
    padding: 5px 0;
  }

  .field-label {
    margin-bottom: 0;

    font-size: 7.5px;
  }

  .social-input {
    padding-top: 1px;
    padding-bottom: 1px;

    font-size: 11px;
    line-height: 1.25;
  }

  .valid-icon {
    width: 21px;
    height: 21px;

    margin-right: 9px;
  }

  .valid-icon svg {
    width: 13px;
    height: 13px;
  }

  .clear-button {
    width: 22px;
    height: 22px;

    margin-right: 9px;

    border-radius: 7px;
  }

  .clear-button svg {
    width: 13px;
    height: 13px;
  }

  .normalizing-loader {
    width: 17px;
    height: 17px;

    margin-right: 11px;

    border-width: 1.5px;
  }

  .field-error {
    gap: 4px;

    margin:
      3px
      0
      0
      9px;

    font-size: 8px;
    line-height: 1.25;
  }

  .field-error svg {
    width: 11px;
    height: 11px;
  }

  /* Hint */

  .socials-hint {
    gap: 7px;

    margin-top: 8px;
    padding: 8px 10px;

    border-radius: 9px;
  }

  .hint-icon,
  .hint-icon svg {
    width: 14px;
    height: 14px;
  }

  .socials-hint p {
    font-size: 8.5px;
    line-height: 1.35;
  }
}

/* ==========================================
   Extra compact
   1366x768
   ========================================== */

@media (min-width: 701px) and (max-height: 770px) {
  .step-card {
    padding:
      12px
      22px;
  }

  .step-header {
    margin-bottom: 6px;
  }

  .step-icon {
    width: 34px;
    height: 34px;
  }

  .step-title {
    font-size: 14px;
  }

  .description {
    margin-bottom: 6px;

    font-size: 9.5px;
    line-height: 1.25;
  }

  .socials-heading {
    margin-bottom: 4px;
  }

  .fields {
    gap: 5px;
  }

  .social-field {
    min-height: 42px;

    border-radius: 10px;
  }

  .social-icon-wrapper {
    width: 29px;
    height: 29px;

    margin-left: 6px;

    border-radius: 8px;
  }

  .social-icon {
    width: 17px;
    height: 17px;
  }

  .separator {
    height: 21px;

    margin:
      0
      9px;
  }

  .input-content {
    padding: 4px 0;
  }

  .field-label {
    font-size: 7px;
  }

  .social-input {
    font-size: 10.5px;
  }

  .valid-icon {
    width: 19px;
    height: 19px;

    margin-right: 8px;
  }

  .valid-icon svg {
    width: 12px;
    height: 12px;
  }

  .clear-button {
    width: 20px;
    height: 20px;

    margin-right: 8px;
  }

  .clear-button svg {
    width: 12px;
    height: 12px;
  }

  .normalizing-loader {
    width: 15px;
    height: 15px;

    margin-right: 9px;
  }

  .field-error {
    margin-top: 2px;

    font-size: 7.5px;
  }

  .socials-hint {
    margin-top: 6px;

    padding:
      6px
      9px;
  }

  .socials-hint p {
    font-size: 8px;
    line-height: 1.25;
  }

  .hint-icon,
  .hint-icon svg {
    width: 13px;
    height: 13px;
  }
}

/* ==========================================
   Mobile
   ========================================== */

@media (max-width: 700px) {
  .step-card {
    padding: 21px 18px;

    border-radius: 20px;
  }

  .step-icon {
    width: 40px;
    height: 40px;

    border-radius: 13px;
  }

  .step-title {
    font-size: 17px;
  }

  .description {
    font-size: 13px;
  }

  .social-field {
    min-height: 59px;
  }

  .social-icon-wrapper {
    width: 40px;
    height: 40px;
  }
}

@media (max-width: 430px) {
  .optional-badge {
    display: none;
  }

  .step-header {
    gap: 10px;
  }

  .description {
    line-height: 1.5;
  }

  .fields {
    gap: 9px;
  }

  .social-icon-wrapper {
    width: 36px;
    height: 36px;

    margin-left: 7px;
  }

  .social-icon {
    width: 20px;
    height: 20px;
  }

  .separator {
    margin:
      0
      10px;
  }

  .field-label {
    font-size: 8px;
  }

  .social-input {
    font-size: 12px;
  }

  .valid-icon {
    margin-right: 9px;
  }

  .clear-button {
    margin-right: 8px;
  }

  .socials-hint {
    padding:
      11px
      12px;
  }
}

/* Touch devices */

@media (hover: none) {
  .social-field:hover {
    transform: none;
  }
}

/* Reduced motion */

@media (prefers-reduced-motion: reduce) {
  .step-card,
  .normalizing-loader {
    animation: none;
  }

  .social-field,
  .social-icon-wrapper,
  .social-icon,
  .separator,
  .clear-button,
  .action-enter-active,
  .action-leave-active,
  .message-enter-active,
  .message-leave-active {
    transition: none;
  }
}
</style>